import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  checkMidShiftReplanEnabled,
  selectIsMidShiftReplanEnabled,
  setMidShiftReplanEnabled,
} from '../store/slices/v2/midShiftReplanFeatureSlice';
import { isLocalEnvironment } from '../utils/environmentDetection';

/**
 * Resolves the RCC_MID_SHIFT_REPLAN flag into Redux for the given station, so every surface (pages,
 * chart, connected Process Path table) reads the same value from the store. Follows
 * {@link useResolvePublishedShiftPlanV2}: local dev short-circuits to `true`; with no station yet it
 * resolves station-less so consumers never wait forever.
 *
 * The flag is site-gated, so it is (re-)checked whenever the station differs from the one last
 * checked (`latestRequestedStation` in the slice): station-less → real site, or one site → another.
 * Keeping that in the store means each station is checked once for the whole app, not once per page.
 *
 * Returns true only once the flag has resolved to enabled (fail closed while pending or on error).
 */
export const useResolveMidShiftReplan = (stationCode: string | null | undefined): boolean => {
  const dispatch = useAppDispatch();
  const flag = useAppSelector((state) => state.midShiftReplanFeature?.isMidShiftReplanEnabled ?? null);
  const latestRequestedStation = useAppSelector(
    (state) => state.midShiftReplanFeature?.latestRequestedStation ?? null,
  );
  const enabled = useAppSelector(selectIsMidShiftReplanEnabled);

  useEffect(() => {
    if (isLocalEnvironment()) {
      if (flag === null) {
        dispatch(setMidShiftReplanEnabled(true));
      }
      return;
    }

    if (stationCode) {
      if (latestRequestedStation !== stationCode) {
        dispatch(checkMidShiftReplanEnabled(stationCode));
      }
      return;
    }

    if (flag === null && latestRequestedStation === null) {
      dispatch(checkMidShiftReplanEnabled(''));
    }
  }, [dispatch, flag, latestRequestedStation, stationCode]);

  return enabled;
};
